import { useState, useEffect, useMemo, useCallback } from "react";
import { GenerateVizaButton } from "./GenerateVizaButton";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import {
  Loader2,
  HeartPulse,
  Send,
  Activity,
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
  Settings,
  Inbox,
  Save,
  Wifi,
  WifiOff,
  AlertTriangle,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FETCH_OPTS = { credentials: "include" };
const FETCH_POST = { credentials: "include", method: "POST" };

// === STATUS WORKFLOW === //
const STATUS_INFO = {
  new: { label: "Neînceput", color: "bg-slate-100 text-slate-700", icon: Clock },
  exported: { label: "Trimis HL7", color: "bg-blue-100 text-blue-800", icon: Send },
  received: { label: "Rezultat primit", color: "bg-amber-100 text-amber-800", icon: Inbox },
  completat: { label: "Completat", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  failed: { label: "Eroare", color: "bg-red-100 text-red-800", icon: XCircle },
};

// Status badge HL7 message
const HL7_STATUS = {
  pending: { color: "bg-amber-100 text-amber-800", icon: Clock, label: "În curs" },
  sent: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Trimis (ACK)" },
  failed: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Eșuat" },
  rejected: { color: "bg-orange-100 text-orange-800", icon: AlertTriangle, label: "Respins" },
  received: { color: "bg-blue-100 text-blue-800", icon: Inbox, label: "Recepționat" },
};

export const Atelier3Button = ({ patient, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [viewMsg, setViewMsg] = useState(null);

  const a3 = patient?.atelier_3 || {};
  const numar = patient?.numar_ordine;
  const status = a3.status || "new";

  // Form state pentru valorile ECG (manuale sau auto-fill din ORU)
  const [form, setForm] = useState(() => ({
    hr: a3.hr || "",
    pr_interval: a3.pr_interval || "",
    qrs_duration: a3.qrs_duration || "",
    qt_interval: a3.qt_interval || "",
    qtc_interval: a3.qtc_interval || "",
    p_axis: a3.p_axis || "",
    qrs_axis: a3.qrs_axis || "",
    t_axis: a3.t_axis || "",
    interpretare_schiller: a3.interpretare_schiller || "",
    diagnostic_medic: a3.diagnostic_medic || "",
    observatii: a3.observatii || "",
    traseu_limite_normale: a3.traseu_limite_normale || false,
    traseu_limite_normale_av: a3.traseu_limite_normale_av || "",
    revenire_ekg: a3.revenire_ekg || false,
    revenire_ekg_zile: a3.revenire_ekg_zile || "",
    in_observatie_ekg: a3.in_observatie_ekg || false,
  }));

  // Re-sync când se deschide
  useEffect(() => {
    if (open) {
      setForm({
        hr: a3.hr || "",
        pr_interval: a3.pr_interval || "",
        qrs_duration: a3.qrs_duration || "",
        qt_interval: a3.qt_interval || "",
        qtc_interval: a3.qtc_interval || "",
        p_axis: a3.p_axis || "",
        qrs_axis: a3.qrs_axis || "",
        t_axis: a3.t_axis || "",
        interpretare_schiller: a3.interpretare_schiller || "",
        diagnostic_medic: a3.diagnostic_medic || "",
        observatii: a3.observatii || "",
        traseu_limite_normale: a3.traseu_limite_normale || false,
        traseu_limite_normale_av: a3.traseu_limite_normale_av || "",
        revenire_ekg: a3.revenire_ekg || false,
        revenire_ekg_zile: a3.revenire_ekg_zile || "",
        in_observatie_ekg: a3.in_observatie_ekg || false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // === API helpers === //
  const fetchConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API}/integrations/schiller-hl7/config`, FETCH_OPTS);
      if (r.ok) setConfig(await r.json());
    } catch (e) {
      console.error("[Atelier3 HL7] fetch config", e);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    if (!patient?.id) return;
    setLoadingLogs(true);
    try {
      const r = await fetch(`${API}/integrations/schiller-hl7/logs?patient_id=${patient.id}&limit=20`, FETCH_OPTS);
      if (r.ok) setLogs(await r.json());
    } catch (e) {
      console.error("[Atelier3 HL7] fetch logs", e);
    } finally {
      setLoadingLogs(false);
    }
  }, [patient?.id]);

  useEffect(() => {
    if (open) {
      fetchConfig();
      fetchLogs();
    }
  }, [open, fetchConfig, fetchLogs]);

  // === Actions === //
  const handleSendHL7 = async () => {
    if (!config?.enabled) {
      toast.error("Integrarea HL7 nu este activată. Configureaz-o întâi.");
      setConfigOpen(true);
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/integrations/schiller-hl7/send-orm/${patient.id}`, FETCH_POST);
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const log = await r.json();
      toast.success(
        <div>
          <div className="font-semibold">ORM^O01 trimis</div>
          <div className="text-xs mt-1">Visit ID: <code>{log.visit_id}</code></div>
        </div>,
        { duration: 6000 }
      );
      // Marchează status în atelier_3
      await updateA3({
        status: "exported",
        exported_at: new Date().toISOString(),
        export_visit_id: log.visit_id,
        export_format: "hl7",
      }, false);
      // Refresh logs
      setTimeout(fetchLogs, 600);
      setTimeout(fetchLogs, 2500);
    } catch (e) {
      toast.error(`Eroare la trimitere HL7: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleRetry = async (logId) => {
    try {
      const r = await fetch(`${API}/integrations/schiller-hl7/retry/${logId}`, FETCH_POST);
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      toast.success("Re-trimitere inițiată");
      setTimeout(fetchLogs, 600);
      setTimeout(fetchLogs, 2500);
    } catch (e) {
      toast.error(`Retry eșuat: ${e.message}`);
    }
  };

  const handlePreview = async () => {
    try {
      const r = await fetch(`${API}/integrations/schiller-hl7/preview-orm/${patient.id}`, FETCH_POST);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const text = await r.text();
      setViewMsg({ title: "Preview ORM^O01 (nu se trimite)", content: text });
    } catch (e) {
      toast.error(`Preview eșuat: ${e.message}`);
    }
  };

  const updateA3 = async (patchPartial, showToast = true) => {
    try {
      const r = await fetch(`${API}/patients/${patient.id}/atelier/3`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchPartial),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const updatedPatient = await r.json();
      onUpdate && onUpdate(updatedPatient);
      if (showToast) toast.success("Atelier 3 salvat");
      return updatedPatient;
    } catch (e) {
      if (showToast) toast.error(`Eroare salvare: ${e.message}`);
      throw e;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert empty strings to 0 for numeric fields
      const toNum = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
      const payload = {
        hr: toNum(form.hr),
        pr_interval: toNum(form.pr_interval),
        qrs_duration: toNum(form.qrs_duration),
        qt_interval: toNum(form.qt_interval),
        qtc_interval: toNum(form.qtc_interval),
        p_axis: toNum(form.p_axis),
        qrs_axis: toNum(form.qrs_axis),
        t_axis: toNum(form.t_axis),
        interpretare_schiller: form.interpretare_schiller,
        diagnostic_medic: form.diagnostic_medic,
        observatii: form.observatii,
        traseu_limite_normale: !!form.traseu_limite_normale,
        traseu_limite_normale_av: form.traseu_limite_normale ? toNum(form.traseu_limite_normale_av) : null,
        revenire_ekg: !!form.revenire_ekg,
        revenire_ekg_zile: form.revenire_ekg ? toNum(form.revenire_ekg_zile) : null,
        in_observatie_ekg: !!form.in_observatie_ekg,
        completat: true,
        data_completare: new Date().toISOString(),
        status: "completat",
      };
      await updateA3(payload);
    } finally {
      setSaving(false);
    }
  };

  // === Latest received observations din ORU === //
  const latestReceived = useMemo(() => {
    return logs.find((l) => l.direction === "inbound" && l.status === "received");
  }, [logs]);

  const handleAutoFillFromORU = () => {
    if (!latestReceived?.payload) {
      toast.warning("Nu există rezultat ORU primit pentru acest pacient");
      return;
    }
    // Parse simplu OBX segments
    const segments = latestReceived.payload.split("\r");
    const obxFields = {};
    for (const seg of segments) {
      if (!seg.startsWith("OBX|")) continue;
      const parts = seg.split("|");
      const id = (parts[3] || "").split("^")[0];
      const val = parts[5] || "";
      obxFields[id] = val;
    }
    const map = {
      HR: "hr",
      PR: "pr_interval",
      QRS: "qrs_duration",
      QT: "qt_interval",
      QTC: "qtc_interval",
      "P-AXIS": "p_axis",
      "QRS-AXIS": "qrs_axis",
      "T-AXIS": "t_axis",
      INTERP: "interpretare_schiller",
    };
    let filled = 0;
    const newForm = { ...form };
    for (const [hl7Id, formKey] of Object.entries(map)) {
      if (obxFields[hl7Id]) {
        newForm[formKey] = obxFields[hl7Id];
        filled++;
      }
    }
    setForm(newForm);
    toast.success(`${filled} valori auto-completate din ORU`);
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const StatusBadge = STATUS_INFO[status] || STATUS_INFO.new;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            data-testid="atelier-3-btn"
          >
            <HeartPulse className="w-4 h-4 text-rose-600" />
            <span>Atelier 3 (ECG SCHILLER HL7)</span>
            <Badge className={`ml-auto ${StatusBadge.color}`} variant="outline">
              <StatusBadge.icon className="w-3 h-3 mr-1" />
              {StatusBadge.label}
            </Badge>
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-rose-600" />
              Atelier 3 — Investigație ECG (integrare HL7 v2.5 SCHILLER SEMA)
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Pacient #{numar} · {patient?.date_personale?.nume} {patient?.date_personale?.prenume}
            </p>
          </DialogHeader>

          {/* === HL7 STATUS & ACTIONS === */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />
                  Conexiune HL7 SCHILLER
                </CardTitle>
                <ConfigStatus config={config} onOpen={() => setConfigOpen(true)} />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2" data-testid="atelier3-hl7-actions">
                <Button
                  onClick={handleSendHL7}
                  disabled={sending || !config?.enabled}
                  className="gap-2"
                  data-testid="hl7-send-orm-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Trimite worklist ECG (ORM^O01)
                </Button>
                <Button variant="outline" onClick={handlePreview} className="gap-2" data-testid="hl7-preview-btn">
                  <Eye className="w-4 h-4" />
                  Preview mesaj HL7
                </Button>
                <Button variant="outline" onClick={fetchLogs} className="gap-2" data-testid="hl7-refresh-btn">
                  <RefreshCw className={`w-4 h-4 ${loadingLogs ? "animate-spin" : ""}`} />
                  Refresh log
                </Button>
              </div>

              {/* HL7 message log */}
              <div className="border rounded-lg overflow-hidden" data-testid="hl7-log-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-2 font-medium">Direcție</th>
                      <th className="text-left p-2 font-medium">Tip</th>
                      <th className="text-left p-2 font-medium">Status</th>
                      <th className="text-left p-2 font-medium">Visit ID</th>
                      <th className="text-left p-2 font-medium">Creat</th>
                      <th className="text-left p-2 font-medium">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 && (
                      <tr><td colSpan={6} className="p-3 text-center text-slate-500 italic">Niciun mesaj HL7 pentru acest pacient</td></tr>
                    )}
                    {logs.map((l) => {
                      const s = HL7_STATUS[l.status] || HL7_STATUS.pending;
                      const Icon = s.icon;
                      return (
                        <tr key={l.id} className="border-b last:border-b-0 hover:bg-slate-50">
                          <td className="p-2">
                            {l.direction === "outbound" ? (
                              <span className="text-blue-700 font-medium">→ Send</span>
                            ) : (
                              <span className="text-emerald-700 font-medium">← Recv</span>
                            )}
                          </td>
                          <td className="p-2 font-mono text-[10px]">{l.message_type}</td>
                          <td className="p-2">
                            <Badge className={s.color} variant="outline">
                              <Icon className="w-3 h-3 mr-1" />
                              {s.label}
                              {l.attempts > 0 && ` (${l.attempts})`}
                            </Badge>
                          </td>
                          <td className="p-2 font-mono text-[10px]">{l.visit_id?.slice(0, 8) || "—"}</td>
                          <td className="p-2 text-slate-600">{formatDateTime(l.created_at)}</td>
                          <td className="p-2 flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setViewMsg({ title: `${l.message_type} (${l.direction})`, content: l.payload, response: l.response, error: l.error })}
                              className="h-6 px-2"
                              data-testid={`hl7-view-${l.id}`}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            {l.direction === "outbound" && (l.status === "failed" || l.status === "rejected") && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRetry(l.id)}
                                className="h-6 px-2 text-blue-700"
                                data-testid={`hl7-retry-${l.id}`}
                              >
                                <RefreshCw className="w-3 h-3" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {latestReceived && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center justify-between">
                  <div className="text-xs text-emerald-900">
                    <strong>Rezultat ECG disponibil</strong> — primit la {formatDateTime(latestReceived.created_at)}
                  </div>
                  <Button size="sm" variant="outline" onClick={handleAutoFillFromORU} data-testid="hl7-autofill-btn">
                    Auto-completare valori
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* === ECG MEASUREMENTS === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <HeartPulse className="w-4 h-4 text-rose-600" />
                Valori ECG
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <NumField label="HR (bpm)" value={form.hr} onChange={(v) => setF("hr", v)} testId="ecg-hr" />
              <NumField label="PR (ms)" value={form.pr_interval} onChange={(v) => setF("pr_interval", v)} testId="ecg-pr" />
              <NumField label="QRS (ms)" value={form.qrs_duration} onChange={(v) => setF("qrs_duration", v)} testId="ecg-qrs" />
              <NumField label="QT (ms)" value={form.qt_interval} onChange={(v) => setF("qt_interval", v)} testId="ecg-qt" />
              <NumField label="QTc (ms)" value={form.qtc_interval} onChange={(v) => setF("qtc_interval", v)} testId="ecg-qtc" />
              <NumField label="Ax P (°)" value={form.p_axis} onChange={(v) => setF("p_axis", v)} testId="ecg-p-axis" />
              <NumField label="Ax QRS (°)" value={form.qrs_axis} onChange={(v) => setF("qrs_axis", v)} testId="ecg-qrs-axis" />
              <NumField label="Ax T (°)" value={form.t_axis} onChange={(v) => setF("t_axis", v)} testId="ecg-t-axis" />
            </CardContent>
          </Card>

          {/* === INTERPRETATION === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Interpretare</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Auto-interpretare SCHILLER</Label>
                <Textarea
                  value={form.interpretare_schiller}
                  onChange={(e) => setF("interpretare_schiller", e.target.value)}
                  rows={2}
                  placeholder="Auto-completat din ORU sau introdus manual"
                  data-testid="ecg-interpret-schiller"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Diagnostic medic</Label>
                <Textarea
                  value={form.diagnostic_medic}
                  onChange={(e) => setF("diagnostic_medic", e.target.value)}
                  rows={2}
                  placeholder="Confirmare / completare medic"
                  data-testid="ecg-diagnostic-medic"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Observații</Label>
                <Textarea
                  value={form.observatii}
                  onChange={(e) => setF("observatii", e.target.value)}
                  rows={2}
                  data-testid="ecg-observatii"
                />
              </div>

              {/* Concluzii traseu / revenire EKG (se propagă în Concluzii aviz medico-sportiv) */}
              <div className="space-y-2 pt-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Checkbox
                    id="ecg-traseu-limite-normale"
                    checked={!!form.traseu_limite_normale}
                    onCheckedChange={(v) => setF("traseu_limite_normale", !!v)}
                    data-testid="ecg-traseu-limite-normale-check"
                  />
                  <Label htmlFor="ecg-traseu-limite-normale" className="text-sm cursor-pointer">
                    Traseu în limite normale cu AV:
                  </Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    className="h-8 w-24"
                    value={form.traseu_limite_normale_av}
                    onChange={(e) => setF("traseu_limite_normale_av", e.target.value)}
                    disabled={!form.traseu_limite_normale}
                    placeholder="AV"
                    data-testid="ecg-traseu-limite-normale-av"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Checkbox
                    id="ecg-revenire-ekg"
                    checked={!!form.revenire_ekg}
                    onCheckedChange={(v) => setF("revenire_ekg", !!v)}
                    data-testid="ecg-revenire-ekg-check"
                  />
                  <Label htmlFor="ecg-revenire-ekg" className="text-sm cursor-pointer">
                    Revenire la EKG la:
                  </Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    className="h-8 w-24"
                    value={form.revenire_ekg_zile}
                    onChange={(e) => setF("revenire_ekg_zile", e.target.value)}
                    disabled={!form.revenire_ekg}
                    placeholder="zile"
                    data-testid="ecg-revenire-ekg-zile"
                  />
                  <span className="text-sm text-slate-600">zile</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Checkbox
                    id="ecg-in-observatie"
                    checked={!!form.in_observatie_ekg}
                    onCheckedChange={(v) => setF("in_observatie_ekg", !!v)}
                    data-testid="ecg-in-observatie-check"
                  />
                  <Label htmlFor="ecg-in-observatie" className="text-sm cursor-pointer">
                    În observație pentru modificări EKG
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* === SAVE === */}
          <div className="flex justify-end items-center gap-2 pt-3 border-t">
            <GenerateVizaButton patient={patient} onGenerated={onUpdate} />
            <Button variant="outline" onClick={() => setOpen(false)}>Închide</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="atelier3-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvează (marchează completat)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} initial={config} onSaved={(c) => { setConfig(c); fetchConfig(); }} />
      <MessageViewer data={viewMsg} onClose={() => setViewMsg(null)} />
    </>
  );
};

// === Subcomponents === //
const NumField = ({ label, value, onChange, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <Input type="number" step="0.1" value={value} onChange={(e) => onChange(e.target.value)} className="h-8" data-testid={testId} />
  </div>
);

const ConfigStatus = ({ config, onOpen }) => {
  const enabled = config?.enabled;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onOpen}
      className={`h-7 px-2 gap-1.5 text-xs ${enabled ? "text-emerald-700 hover:bg-emerald-50" : "text-slate-500 hover:bg-slate-100"}`}
      data-testid="hl7-config-btn"
    >
      {enabled ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
      {config ? (
        <span>
          {enabled ? "Activ" : "Inactiv"} — {config.host}:{config.port}
        </span>
      ) : (
        <span>Încarcă...</span>
      )}
      <Settings className="w-3 h-3 ml-1" />
    </Button>
  );
};

const ConfigDialog = ({ open, onClose, initial, onSaved }) => {
  const [c, setC] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { if (open) { setC(initial); setTestResult(null); } }, [open, initial]);

  if (!c) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/integrations/schiller-hl7/config`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...c, port: parseInt(c.port, 10) || 6661, timeout_seconds: parseInt(c.timeout_seconds, 10) || 10, max_retries: parseInt(c.max_retries, 10) || 3 }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const saved = await r.json();
      toast.success("Configurare salvată");
      onSaved && onSaved(saved);
      onClose();
    } catch (e) {
      toast.error(`Eroare: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!c.host || !c.port) {
      toast.error("Completați IP-ul și portul înainte de a testa.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/integrations/schiller-hl7/test-connection`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: c.host,
          port: parseInt(c.port, 10) || 6661,
          timeout_seconds: parseInt(c.timeout_seconds, 10) || 5,
        }),
      });
      const res = await r.json();
      setTestResult(res);
      if (res.success) {
        toast.success(`Conexiune OK (${res.latency_ms}ms)`);
      } else {
        toast.error(res.message || "Conexiune eșuată");
      }
    } catch (e) {
      const failResult = { success: false, message: `Eroare client: ${e.message}` };
      setTestResult(failResult);
      toast.error(failResult.message);
    } finally {
      setTesting(false);
    }
  };

  const fld = (k, v) => setC((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Configurare HL7 SCHILLER SEMA
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3" data-testid="hl7-config-form">
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">SCHILLER Server IP (static din rețeaua clinicii) *</Label>
            <Input value={c.host || ""} onChange={(e) => fld("host", e.target.value)} data-testid="hl7-cfg-host" placeholder="ex: 192.168.1.100" />
            <p className="text-xs text-muted-foreground">IP-ul fix al serverului SCHILLER SEMA din LAN-ul clinicii (configurat de IT-ul clinicii).</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Port MLLP *</Label>
            <Input type="number" value={c.port || ""} onChange={(e) => fld("port", e.target.value)} data-testid="hl7-cfg-port" placeholder="ex: 6661" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Timeout (s)</Label>
            <Input type="number" value={c.timeout_seconds || ""} onChange={(e) => fld("timeout_seconds", e.target.value)} data-testid="hl7-cfg-timeout" />
          </div>
          <Separator className="col-span-2" />
          <div className="space-y-1">
            <Label className="text-xs">Sending Application</Label>
            <Input value={c.sending_application || ""} onChange={(e) => fld("sending_application", e.target.value)} data-testid="hl7-cfg-sending-app" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Sending Facility</Label>
            <Input value={c.sending_facility || ""} onChange={(e) => fld("sending_facility", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Receiving Application</Label>
            <Input value={c.receiving_application || ""} onChange={(e) => fld("receiving_application", e.target.value)} data-testid="hl7-cfg-receiving-app" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Receiving Facility</Label>
            <Input value={c.receiving_facility || ""} onChange={(e) => fld("receiving_facility", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max retries</Label>
            <Input type="number" value={c.max_retries || ""} onChange={(e) => fld("max_retries", e.target.value)} data-testid="hl7-cfg-retries" />
          </div>
          <div className="flex items-end gap-2 col-span-2 pt-2 border-t flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!c.enabled}
                onChange={(e) => fld("enabled", e.target.checked)}
                className="w-4 h-4"
                data-testid="hl7-cfg-enabled"
              />
              <span className="text-sm font-medium">Activează integrarea HL7</span>
            </label>
            <div className="flex-1" />
            <Button
              type="button"
              variant="outline"
              onClick={testConnection}
              disabled={testing || !c.host || !c.port}
              data-testid="hl7-cfg-test-btn"
              className="gap-2"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
              Testează conexiunea
            </Button>
            <Button variant="outline" onClick={onClose}>Anulează</Button>
            <Button onClick={save} disabled={saving} data-testid="hl7-cfg-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvează
            </Button>
          </div>
          {testResult && (
            <div
              className={`col-span-2 p-3 rounded-md text-sm flex items-start gap-2 ${
                testResult.success
                  ? "bg-emerald-50 border border-emerald-200 text-emerald-900"
                  : "bg-red-50 border border-red-200 text-red-900"
              }`}
              data-testid="hl7-test-result"
            >
              {testResult.success ? (
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-600 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="font-medium">
                  {testResult.success ? "Conexiune reușită" : "Conexiune eșuată"}
                  {testResult.latency_ms != null && (
                    <span className="ml-2 text-xs font-normal opacity-80">(latency {testResult.latency_ms}ms)</span>
                  )}
                </p>
                <p className="text-xs mt-0.5 opacity-90">{testResult.message}</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MessageViewer = ({ data, onClose }) => {
  if (!data) return null;
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data.title}</DialogTitle>
        </DialogHeader>
        <pre className="bg-slate-900 text-emerald-300 text-xs p-3 rounded font-mono overflow-x-auto whitespace-pre-wrap" data-testid="hl7-msg-content">
          {(data.content || "").replace(/\r/g, "\n")}
        </pre>
        {data.response && (
          <>
            <div className="text-xs font-semibold mt-2">Răspuns (ACK):</div>
            <pre className="bg-slate-800 text-amber-200 text-xs p-3 rounded font-mono overflow-x-auto whitespace-pre-wrap">{data.response.replace(/\r/g, "\n")}</pre>
          </>
        )}
        {data.error && (
          <>
            <div className="text-xs font-semibold mt-2 text-red-700">Eroare:</div>
            <pre className="bg-red-50 text-red-900 text-xs p-3 rounded font-mono">{data.error}</pre>
          </>
        )}
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
